import { roles } from './roles';

/**
 * Public routes that don't require authentication
 * These routes are accessible without a valid session
 */
export const PUBLIC_ROUTES = new Set([
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'verify-email',
  'callback',
] as const);

/**
 * Valid locales supported by the application
 */
export const VALID_LOCALES = new Set(['es', 'en'] as const);

/**
 * Valid user roles in the system
 */
export const VALID_ROLES = new Set([roles.ADMIN, roles.USER] as const);

/**
 * Default redirect page for authenticated users
 */
export const DEFAULT_REDIRECT_PAGE = 'login';
export const DEFAULT_LOGGED_REDIRECT_PAGE = 'dashboard';

// Type definitions for better type safety
export type TPublicRoute = typeof PUBLIC_ROUTES extends Set<infer U> ? U : never;
export type TValidLocale = typeof VALID_LOCALES extends Set<infer U> ? U : never;
export type TValidRole = typeof VALID_ROLES extends Set<infer U> ? U : never;

/**
 * Check if a route is public
 * @param pathSegments - Array of path segments
 * @returns true if the route is public
 */
export function isPublicRoute(pathSegments: string[]): boolean {
  const lastSegment = pathSegments[pathSegments.length - 1];
  return PUBLIC_ROUTES.has(lastSegment as TPublicRoute);
}

/**
 * Check if a request comes from a public route by examining the referer header
 * @param referer - The referer header value
 * @returns true if the request comes from a public route
 */
export function isRequestFromPublicRoute(referer: string | null): boolean {
  if (!referer) return false;

  try {
    const url = new URL(referer);
    const pathSegments = url.pathname.split('/').filter(Boolean);
    return isPublicRoute(pathSegments);
  } catch {
    return false;
  }
}
